import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PortalPopover from "@/components/PortalPopover";
import { Printer, ArrowLeft, AlertTriangle, Plus, Save, RotateCcw, GripVertical } from "lucide-react";
import { computeShiftedPlatePositions, maxPositionFor, CLOSED_HOLES_DEFAULT, abbreviateLabel } from "@/lib/cartMath";
import CartPrintLabel from "@/components/print/CartPrintLabel";
import { usePrintPage } from "@/lib/usePrintPage";
import { moveLayerToIndex } from "@/lib/cartOrder";

export default function CalculationResult() {
    usePrintPage("zebra-label");
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const autoPrint = searchParams.get("auto_print") === "1";
    const autoPrintLoc = searchParams.get("location_id") || "";
    const [data, setData] = useState(null);
    const [carts, setCarts] = useState(null); // local editable copy
    const [dirty, setDirty] = useState(false);
    const [drag, setDrag] = useState(null); // {cartIdx, layerIdx}
    const [dropTarget, setDropTarget] = useState(null); // {cartIdx, layerIdx}
    const [saving, setSaving] = useState(false);
    const [printFilter, setPrintFilter] = useState(null); // location_id to filter prints, null = all
    const [pendingPrintFilter, setPendingPrintFilter] = useState(null);
    const [locColors, setLocColors] = useState({});
    const [showAddCart, setShowAddCart] = useState(false);
    const [loadError, setLoadError] = useState("");

    const load = useCallback(async () => {
        setLoadError("");
        try {
            const [r, locs] = await Promise.all([
                api.get(`/calculation-runs/${id}`),
                api.get("/locations").catch(() => ({ data: [] })),
            ]);
            setData(r.data);
            setCarts(cloneCarts(r.data.run.carts));
            setLocColors(Object.fromEntries((locs.data || []).map((l) => [l.id, l.color])));
            setDirty(false);
        } catch (e) {
            setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Berekening kon niet worden geladen"));
        }
    }, [id]);
    useEffect(() => {
        load();
    }, [load]);

    // Reset printFilter after print
    useEffect(() => {
        const onAfter = () => setPrintFilter(null);
        window.addEventListener("afterprint", onAfter);
        return () => window.removeEventListener("afterprint", onAfter);
    }, []);

    // Print pas nadat React de gekozen locatie daadwerkelijk in de print-DOM
    // heeft verwerkt. Zo kan een trage render nooit per ongeluk alle karren printen.
    useEffect(() => {
        if (!carts || pendingPrintFilter === null || pendingPrintFilter !== printFilter) return;
        setPendingPrintFilter(null);
        const t = setTimeout(() => window.print(), 0);
        return () => clearTimeout(t);
    }, [carts, pendingPrintFilter, printFilter]);

    // Auto-print when launched from Printcentrum (?auto_print=1[&location_id=...]).
    useEffect(() => {
        if (!autoPrint || !data || !carts) return;
        if (data.run?.is_current === false) return;
        if (autoPrintLoc) setPrintFilter(autoPrintLoc);
        const t = setTimeout(() => window.print(), 500);
        return () => clearTimeout(t);
    }, [autoPrint, autoPrintLoc, data, carts]);

    if (loadError) {
        return (
            <div className="p-12 max-w-2xl mx-auto">
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
                    {loadError}
                    <button type="button" onClick={load} className="ml-3 font-semibold underline">Opnieuw proberen</button>
                </div>
            </div>
        );
    }
    if (!data || !carts) return <div className="p-12 text-ink-500">Laden…</div>;
    const { run, order, customer } = data;

    // --- drag handlers ---
    const onDragStart = (ci, li) => (e) => {
        setDrag({ ci, li });
        e.dataTransfer.effectAllowed = "move";
        // Safari only starts an HTML5 drag when at least one data item exists.
        e.dataTransfer.setData("text/plain", `${ci}:${li}`);
    };
    const onDragEnd = () => {
        setDrag(null);
        setDropTarget(null);
    };
    const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const commitLayerMove = (targetCi, targetLi) => {
        if (!drag) return;
        const next = cloneCarts(carts);
        const sourceCart = next[drag.ci];
        const targetCart = next[targetCi];
        const sourceLayer = sourceCart?.layers?.[drag.li];
        if (!sourceLayer || !targetCart) return;

        const normalizedTargetLi = drag.ci === targetCi
            ? Math.max(0, Math.min(targetLi, targetCart.layers.length - 1))
            : targetLi;
        if (drag.ci === targetCi && drag.li === normalizedTargetLi) return;

        if (
            drag.ci !== targetCi
            && sourceLayer.location_id
            && targetCart.location_id
            && sourceLayer.location_id !== targetCart.location_id
        ) {
            toast.error("Lagen van verschillende locaties mogen niet op dezelfde kar.");
            return;
        }

        if (drag.ci === targetCi) {
            targetCart.layers = moveLayerToIndex(targetCart.layers, drag.li, normalizedTargetLi);
        } else {
            const [moved] = sourceCart.layers.splice(drag.li, 1);
            const insertAt = Math.max(0, Math.min(targetLi, targetCart.layers.length));
            targetCart.layers.splice(insertAt, 0, moved);
            if (!targetCart.location_id && moved.location_id) {
                targetCart.location_id = moved.location_id;
                targetCart.location_code = moved.location_code;
                targetCart.location_name = moved.location_name;
            }
        }

        const cleaned = next.filter((cart) => cart.layers.length > 0);
        renumber(cleaned);
        setCarts(cleaned);
        setDirty(true);
    };

    const onDropCart = (targetCi) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!drag) return;
        // Dropping on the card itself means: place this layer on top.
        commitLayerMove(targetCi, carts[targetCi]?.layers?.length ?? 0);
        setDrag(null);
        setDropTarget(null);
    };

    const onDropLayer = (targetCi, targetLi) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!drag) return;
        commitLayerMove(targetCi, targetLi);
        setDrag(null);
        setDropTarget(null);
    };

    const addEmptyCart = (locId, locCode, locName) => {
        const next = cloneCarts(carts);
        next.push({
            cart_no: next.length + 1,
            layers: [],
            total_units: 0,
            products_on_cart: [],
            location_id: locId || "",
            location_code: locCode || "",
            location_name: locName || "",
            risers: !!run?.summary?.risers,
        });
        setCarts(next);
        setDirty(true);
    };

    const toggleCartRisers = (ci) => {
        const next = cloneCarts(carts);
        const turningOff = !!next[ci].risers;
        next[ci].risers = !next[ci].risers;
        if (turningOff && cartHasOverflow(next[ci])) {
            toast.error("Opzetstukken kunnen niet uit: deze kar is dan te hoog.");
            return;
        }
        setCarts(next);
        setDirty(true);
    };

    // ────── Lagen samenvoegen ──────
    // Combineer twee (of meer) partial layers tot één combo-laag.
    // De hoogte van de combo wordt door de plant met het hoogste gap-profiel bepaald.
    const mergeLayers = (ci, srcIdx, tgtIdx) => {
        if (srcIdx === tgtIdx) return;
        const next = cloneCarts(carts);
        const cart = next[ci];
        const src = cart.layers[srcIdx];
        const tgt = cart.layers[tgtIdx];
        const itemsFrom = (l) =>
            l.merged_items && l.merged_items.length >= 2
                ? l.merged_items
                : [
                      {
                          product_id: l.product_id,
                          product_sku: l.product_sku,
                          product_name: l.product_name,
                          product_cultivar: l.product_cultivar || "",
                          pot_size: l.pot_size || "",
                          quantity: l.quantity,
                          units_per_layer: l.units_per_layer,
                          gap_units: l.gap_units || 0,
                          height_profile_code: l.height_profile_code || "",
                          height_profile_name: l.height_profile_name || "",
                      },
                  ];
        const items = [...itemsFrom(tgt), ...itemsFrom(src)].map((it) => ({
            ...it,
            fill_fraction:
                (it.quantity || 0) / Math.max(1, it.units_per_layer || 1),
        }));
        const totalFill = items.reduce((s, it) => s + it.fill_fraction, 0);
        if (totalFill > 1.0001) {
            toast.error(
                `Past niet: combinatie zou ${(totalFill * 100).toFixed(0)}% van een laag beslaan (max 100%)`
            );
            return;
        }
        const top = items.reduce(
            (a, it) => ((it.gap_units || 0) > (a.gap_units || 0) ? it : a),
            items[0]
        );
        cart.layers[tgtIdx] = {
            ...tgt,
            merged_items: items,
            quantity: items.reduce((s, it) => s + (it.quantity || 0), 0),
            gap_units: top.gap_units || 0,
            height_profile_code: top.height_profile_code || "",
            height_profile_name: top.height_profile_name || "",
            product_id: top.product_id,
            product_sku: items.map((it) => it.product_sku).join(" + "),
            product_name: items.map((it) => it.product_name).join(" + "),
            product_cultivar: "",
            is_partial: totalFill < 0.9999,
        };
        cart.layers.splice(srcIdx, 1);
        setCarts(next);
        setDirty(true);
        toast.success(
            `${items.length} planten samengevoegd op laag · ${(totalFill * 100).toFixed(0)}% vol`
        );
    };

    const unmergeLayer = (ci, li) => {
        const next = cloneCarts(carts);
        const cart = next[ci];
        const layer = cart.layers[li];
        if (!layer.merged_items || layer.merged_items.length < 2) return;
        const newLayers = layer.merged_items.map((it) => ({
            ...layer,
            product_id: it.product_id,
            product_sku: it.product_sku,
            product_name: it.product_name,
            product_cultivar: it.product_cultivar || "",
            pot_size: it.pot_size || "",
            quantity: it.quantity,
            units_per_layer: it.units_per_layer,
            gap_units: it.gap_units || 0,
            height_profile_code: it.height_profile_code || "",
            height_profile_name: it.height_profile_name || "",
            is_partial:
                (it.quantity || 0) < (it.units_per_layer || 0),
            merged_items: [],
        }));
        cart.layers.splice(li, 1, ...newLayers);
        setCarts(next);
        setDirty(true);
        toast.success("Samenvoeging ongedaan gemaakt");
    };

    const reset = () => {
        setCarts(cloneCarts(run.carts));
        setDirty(false);
    };

    const save = async () => {
        const invalid = carts.filter((cart) => cartHasOverflow(cart) || cartHasMixedLocations(cart));
        if (invalid.length > 0) {
            toast.error("Los eerst de rood gemarkeerde of gemengde karren op.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                carts: carts
                    .filter((c) => c.layers.length > 0)
                    .map((c) => ({
                        cart_no: c.cart_no,
                        location_id: c.location_id || "",
                        location_code: c.location_code || "",
                        location_name: c.location_name || "",
                        risers: !!c.risers,
                        layers: c.layers.map((l) => ({
                            product_id: l.product_id,
                            product_sku: l.product_sku,
                            product_name: l.product_name,
                            product_cultivar: l.product_cultivar || "",
                            pot_size: l.pot_size || "",
                            quantity: l.quantity,
                            units_per_layer: l.units_per_layer,
                            height_profile_code: l.height_profile_code,
                            height_profile_name: l.height_profile_name,
                            gap_units: l.gap_units || 0,
                            location_id: l.location_id || "",
                            location_code: l.location_code || "",
                            location_name: l.location_name || "",
                            is_partial: !!l.is_partial,
                            merged_items: l.merged_items || [],
                        })),
                    })),
            };
            const saved = await api.put(`/calculation-runs/${id}`, payload);
            const revisedId = saved.data?.id;
            toast.success("Nieuwe berekeningsrevisie opgeslagen");
            if (revisedId && revisedId !== id) {
                navigate(`/berekening/${revisedId}`, { replace: true });
            } else {
                await load();
            }
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail ?? "Opslaan mislukt"));
        } finally {
            setSaving(false);
        }
    };

    const totals = carts.reduce(
        (acc, c) => {
            acc.layers += c.layers.length;
            acc.units += c.layers.reduce((s, l) => s + l.quantity, 0);
            if (c.layers.length > 0) acc.carts += 1;
            return acc;
        },
        { carts: 0, layers: 0, units: 0 }
    );
    const invalidCartNumbers = carts
        .map((cart, index) => (cartHasOverflow(cart) || cartHasMixedLocations(cart) ? index + 1 : null))
        .filter(Boolean);
    const hasInvalidCarts = invalidCartNumbers.length > 0;

    return (
        <>
            <div className="p-8 lg:p-12 max-w-7xl no-print mx-auto">
                <Link
                    to={`/bestellingen/${order.id}`}
                    className="text-sm text-ink-500 hover:text-florefy-900 inline-flex items-center gap-1 mb-4"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar order
                </Link>

                <PageHeader
                    kicker={`Berekening v${run.version_no}${run.status === "manual" ? " · handmatig" : ""}`}
                    title={`${totals.carts} karren · ${totals.layers} lagen`}
                    subtitle={`${customer?.name || ""} · Leverdatum ${order.delivery_date} · sleep lagen binnen of tussen karren om aan te passen`}
                    testid="calc-header"
                    actions={
                        <>
                            {dirty && (
                                <button
                                    onClick={reset}
                                    data-testid="btn-reset-override"
                                    className="px-3 py-2 rounded-sm border border-border bg-white hover:bg-ink-50 text-sm flex items-center gap-2"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                            )}
                            {dirty && (
                                <button
                                    onClick={save}
                                    disabled={saving || hasInvalidCarts}
                                    title={hasInvalidCarts ? "Los eerst de ongeldige karindeling op" : ""}
                                    data-testid="btn-save-override"
                                    className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 disabled:opacity-50"
                                >
                                    <Save className="h-4 w-4" /> {saving ? "Bezig…" : "Wijzigingen opslaan"}
                                </button>
                            )}
                            <button
                                onClick={() => setShowAddCart(true)}
                                data-testid="btn-add-cart"
                                className="px-3 py-2 rounded-sm border border-border bg-white hover:bg-ink-50 text-sm flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" /> Kar toevoegen
                            </button>
                            <button
                                onClick={() => window.print()}
                                data-testid="btn-print"
                                disabled={dirty || hasInvalidCarts || run.is_current === false}
                                title={
                                    run.is_current === false
                                        ? "Deze berekening is verouderd; bereken de bestelling opnieuw"
                                        : dirty || hasInvalidCarts
                                            ? "Eerst een geldige indeling opslaan"
                                            : ""
                                }
                                className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 disabled:opacity-50"
                            >
                                <Printer className="h-4 w-4" /> Labels afdrukken
                            </button>
                        </>
                    }
                />

                {hasInvalidCarts && (
                    <div className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                        Kar {invalidCartNumbers.join(", ")} past fysiek niet of bevat gemengde locaties. Pas dit aan voordat je opslaat of print.
                    </div>
                )}

                {run.is_current === false && (
                    <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
                        Deze berekening is niet meer actueel. Bereken de bestelling opnieuw voordat je labels print.
                    </div>
                )}

                {run.warnings && run.warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-sm p-4 mb-6 flex gap-3">
                        <AlertTriangle className="h-5 w-5 text-yellow-700 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-medium text-yellow-900 text-sm">Waarschuwingen</div>
                            <ul className="text-sm text-yellow-800 mt-1 list-disc list-inside">
                                {run.warnings.map((w, i) => (
                                    <li key={`warn-${i}-${(w || "").slice(0, 16)}`}>{w}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-4 gap-6 mb-8">
                    <Stat label="Totaal karren" value={totals.carts} testid="sum-carts" />
                    <Stat label="Totaal lagen" value={totals.layers} testid="sum-layers" />
                    <Stat label="Totaal platen" value={Math.max(0, totals.layers - totals.carts)} testid="sum-plates" />
                    <Stat label="Totaal stuks" value={totals.units} testid="sum-units" />
                </div>

                {showAddCart && (
                    <AddCartModal
                        locations={locColors}
                        existingGroups={Array.from(
                            new Map(
                                carts
                                    .filter((c) => c.location_id)
                                    .map((c) => [c.location_id, { id: c.location_id, code: c.location_code, name: c.location_name }])
                            ).values()
                        )}
                        onClose={() => setShowAddCart(false)}
                        onAdd={(locId, locCode, locName) => {
                            addEmptyCart(locId, locCode, locName);
                            setShowAddCart(false);
                        }}
                    />
                )}
                {(() => {
                    // Group carts by location, preserving order of first appearance
                    const groups = [];
                    const idxByLoc = new Map();
                    carts.forEach((c, ci) => {
                        const k = c.location_id || "";
                        if (!idxByLoc.has(k)) {
                            idxByLoc.set(k, groups.length);
                            groups.push({
                                location_id: k,
                                location_code: c.location_code || "—",
                                location_name: c.location_name || "Geen locatie",
                                items: [],
                            });
                        }
                        groups[idxByLoc.get(k)].items.push({ cart: c, ci });
                    });
                    return groups.map((g) => {
                        const groupUnits = g.items.reduce(
                            (s, it) => s + it.cart.layers.reduce((u, l) => u + l.quantity, 0),
                            0
                        );
                        return (
                            <div key={g.location_id || "none"} className="mb-10" data-testid={`cart-group-${g.location_code}`}>
                                <div className="flex items-center justify-between mb-4 pb-2 border-b border-border">
                                    <div className="flex items-center gap-3">
                                        <span
                                            className="inline-block h-3 w-3 rounded-sm border border-ink-200"
                                            style={{
                                                backgroundColor:
                                                    (locColors[g.location_id] || "#e5e7eb"),
                                            }}
                                        />
                                        <h3 className="text-lg font-semibold text-ink-900">
                                            <span className="font-mono text-florefy-900">{g.location_code}</span>
                                            <span className="text-ink-500 font-normal ml-2 text-sm">
                                                {g.location_name}
                                            </span>
                                        </h3>
                                        <span className="font-mono text-[11px] uppercase tracking-widest text-ink-500">
                                            {g.items.length} kar{g.items.length === 1 ? "" : "ren"} · {groupUnits} stuks
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setPrintFilter(g.location_id);
                                            setPendingPrintFilter(g.location_id);
                                        }}
                                        disabled={dirty || hasInvalidCarts || run.is_current === false}
                                        title={
                                            run.is_current === false
                                                ? "Deze berekening is verouderd; bereken de bestelling opnieuw"
                                                : dirty || hasInvalidCarts
                                                    ? "Eerst een geldige indeling opslaan"
                                                    : ""
                                        }
                                        className="px-3 py-1.5 rounded-sm border border-border bg-white hover:bg-ink-50 text-xs flex items-center gap-2 disabled:opacity-50"
                                        data-testid={`btn-print-location-${g.location_code}`}
                                    >
                                        <Printer className="h-3.5 w-3.5" /> Print {g.location_code}
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {g.items.map(({ cart, ci }) => (
                                        <CartCard
                                            key={ci}
                                            cart={cart}
                                            cartIdx={ci}
                                            totalCarts={carts.length}
                                            onDragStart={onDragStart}
                                            onDragEnd={onDragEnd}
                                            onDragOver={onDragOver}
                                            onDrop={onDropCart(ci)}
                                            onDropLayer={onDropLayer}
                                            onLayerDragOver={(layerIdx) => setDropTarget({ cartIdx: ci, layerIdx })}
                                            dragging={drag}
                                            dropTarget={dropTarget}
                                            cartType={order?.cart_type || "CC"}
                                            maxPosition={
                                                (35 + (cart.risers ? 7 : 0))
                                            }
                                            onToggleRisers={() => toggleCartRisers(ci)}
                                            onMergeLayers={(srcIdx, tgtIdx) => mergeLayers(ci, srcIdx, tgtIdx)}
                                            onUnmergeLayer={(li) => unmergeLayer(ci, li)}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    });
                })()}
            </div>

            <div className="print-only">
                {carts
                    .filter((c) => c.layers.length > 0)
                    .filter((c) => printFilter === null || (c.location_id || "") === printFilter)
                    .map((c, i, arr) => (
                        <CartPrintLabel
                            key={`${c.location_id || "no-loc"}-${i}`}
                            cart={{ ...c, cart_no: i + 1 }}
                            totalCarts={arr.length}
                            order={order}
                            customer={customer}
                            cartType={order?.cart_type || "CC"}
                        />
                    ))}
            </div>
        </>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="bg-white border border-border rounded-sm p-5" data-testid={testid}>
            <div className="data-label">{label}</div>
            <div className="text-4xl font-mono font-semibold text-ink-900 mt-2">{value}</div>
        </div>
    );
}

function CartCard({ cart, cartIdx, totalCarts, onDragStart, onDragEnd, onDragOver, onDrop, onDropLayer, onLayerDragOver, dragging, dropTarget, cartType = "CC", maxPosition = 35, onToggleRisers, onMergeLayers, onUnmergeLayer }) {
    const isDropTarget = dragging && dragging.ci !== cartIdx;
    const totalUnits = cart.layers.reduce((s, l) => s + l.quantity, 0);
    // Cumulative PLATE positions from bottom, MET dichte-gaten shift.
    // gaps[0] is onderste laag → platePositions[0] = plaat boven onderste laag (=plaat onder laag 2).
    const gaps = cart.layers.map((l) => l.gap_units || l.stack_rank || 0);
    const platePositions = computeShiftedPlatePositions(gaps);
    const closedHoles = CLOSED_HOLES_DEFAULT;
    const [mergePicker, setMergePicker] = useState(null); // index of source layer for picker
    return (
        <div
            className={`bg-white border rounded-sm overflow-hidden transition-all ${
                isDropTarget ? "border-florefy-900 border-2 shadow-lg" : "border-border"
            }`}
            data-testid={`cart-${cartIdx + 1}`}
            onDragOver={onDragOver}
            onDrop={onDrop}
        >
            <div className="px-4 py-3 border-b border-border bg-florefy-900 text-white flex items-center justify-between">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                        {cartType} Kar
                        {cart.location_code && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-white/20 text-white">
                                Locatie {cart.location_code}
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-lg font-semibold">
                        #{cartIdx + 1} <span className="opacity-60">/ {totalCarts}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                        {Math.max(0, cart.layers.length - 1)} platen · {totalUnits} stuks
                    </div>
                    <div className="font-mono text-lg font-semibold">{cart.layers.length} lagen</div>
                </div>
            </div>
            <div className="px-4 py-2 border-b border-border bg-ink-50 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
                    Max: {maxPosition} posities · dicht: {[...closedHoles].sort((a,b)=>a-b).join(", ")}
                </div>
                {onToggleRisers && (
                    <button
                        type="button"
                        onClick={onToggleRisers}
                        className={`px-2 py-1 rounded-sm text-[10px] font-mono font-semibold uppercase tracking-widest border transition-colors ${
                            cart.risers
                                ? "bg-florefy-900 text-white border-florefy-900"
                                : "bg-white text-ink-700 border-border hover:bg-ink-100"
                        }`}
                        data-testid={`btn-cart-risers-${cartIdx + 1}`}
                        data-selected={cart.risers}
                        title="Opzetstukken voor alleen deze kar"
                    >
                        Opzetstukken {cart.risers ? "AAN" : "UIT"}
                    </button>
                )}
            </div>
            <div className="p-4">
                <div className="cart-frame" data-testid={`cart-frame-${cartIdx + 1}`}>
                    {cart.layers.length === 0 && (
                        <div className="text-center text-ink-500 text-xs font-mono uppercase tracking-wider py-8">
                            Lege kar — sleep hier een laag naartoe
                        </div>
                    )}
                    {cart.layers.map((l, li) => {
                        const platePos = li === 0 ? 0 : platePositions[li - 1];
                        const overflow = platePos > maxPosition;
                        const isMerged = l.merged_items && l.merged_items.length >= 2;
                        const label = isMerged
                            ? `${l.merged_items.length} planten samen`
                            : abbreviateLabel(l.product_name, l.product_cultivar);
                        // Andere partials in dezelfde kar (voor samenvoeg-picker)
                        const otherPartials = cart.layers
                            .map((x, idx) => ({ x, idx }))
                            .filter(({ x, idx }) => idx !== li && idx > 0 && x.is_partial);
                        const isPickerOpen = mergePicker === li;
                        return (
                        <div
                            key={`${cartIdx}-${li}-${l.product_sku}`}
                            draggable
                            onDragStart={onDragStart(cartIdx, li)}
                            onDragEnd={onDragEnd}
                            onDragOver={(e) => {
                                onDragOver(e);
                                e.stopPropagation();
                                onLayerDragOver(li);
                            }}
                            onDrop={onDropLayer(cartIdx, li)}
                            className={`cart-layer cursor-grab active:cursor-grabbing ${
                                l.is_partial ? "cart-layer-partial" : ""
                            } ${overflow ? "outline outline-2 outline-red-500" : ""} ${isMerged ? "!border-florefy-700 !border-solid" : ""} ${
                                dropTarget?.cartIdx === cartIdx && dropTarget?.layerIdx === li && !(dragging?.ci === cartIdx && dragging?.li === li)
                                    ? "ring-2 ring-florefy-500 ring-offset-2 bg-florefy-50"
                                    : ""
                            } ${dragging?.ci === cartIdx && dragging?.li === li ? "opacity-45" : ""}`}
                            data-testid={`cart-${cartIdx + 1}-layer-${li + 1}`}
                            aria-label={`Laag ${li + 1}: ${label}. Sleep om te verplaatsen.`}
                        >
                            <GripVertical className="h-3 w-3 text-ink-300 shrink-0 mr-1" />
                            <div className="flex-1 min-w-0 grid grid-cols-[1.25rem_minmax(0,1fr)_auto_auto] gap-x-2 items-center">
                                {/* Laag-nummer */}
                                <div
                                    className="font-mono text-[11px] font-semibold text-ink-700 text-right"
                                    data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-no`}
                                >
                                    {li + 1}
                                </div>
                                {/* Naam (afgekort + cultivar) + detail */}
                                <div className="min-w-0">
                                    {isMerged ? (
                                        <>
                                            <div
                                                className="flex h-5 w-full rounded-sm overflow-hidden border border-florefy-200"
                                                data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-combo`}
                                            >
                                                {l.merged_items.map((it, ii) => (
                                                    <div
                                                        key={`${it.product_id || it.product_sku || "m"}-${ii}`}
                                                        title={`${it.product_name}${it.product_cultivar ? ` '${it.product_cultivar}'` : ""} — ${it.quantity} stuks (${(it.fill_fraction * 100).toFixed(0)}%)`}
                                                        style={{ flex: it.fill_fraction || 0.01 }}
                                                        className={`text-[10px] font-semibold text-white truncate px-1 leading-5 ${
                                                            ii % 2 === 0 ? "bg-florefy-700" : "bg-florefy-500"
                                                        }`}
                                                    >
                                                        {abbreviateLabel(it.product_name, it.product_cultivar)}
                                                    </div>
                                                ))}
                                                {/* Lege ruimte als nog niet vol */}
                                                {(() => {
                                                    const used = l.merged_items.reduce(
                                                        (s, it) => s + (it.fill_fraction || 0),
                                                        0
                                                    );
                                                    return used < 0.999 ? (
                                                        <div
                                                            style={{ flex: 1 - used }}
                                                            className="bg-ink-50 text-[9px] text-ink-400 text-center leading-5"
                                                        >
                                                            {((1 - used) * 100).toFixed(0)}% vrij
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </div>
                                            <div className="font-mono text-[10px] text-ink-400 truncate leading-tight mt-0.5">
                                                <span data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-pos`}>
                                                    {li === 0 ? "op kar" : `gat ${platePos}/${maxPosition}`}
                                                </span>
                                                <span className="text-ink-300"> · </span>
                                                <span>{l.height_profile_code} (hoogste)</span>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div
                                                className="text-[13px] font-semibold text-ink-900 truncate leading-tight"
                                                data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-name`}
                                                title={l.product_cultivar
                                                    ? `${l.product_name} '${l.product_cultivar}' (${l.product_sku})`
                                                    : `${l.product_name} (${l.product_sku})`}
                                            >
                                                {label}
                                            </div>
                                            <div className="font-mono text-[10px] text-ink-400 truncate leading-tight">
                                                <span data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-pos`}>
                                                    {li === 0 ? "op kar" : `gat ${platePos}/${maxPosition}`}
                                                </span>
                                                <span className="text-ink-300"> · </span>
                                                <span>{l.height_profile_code}</span>
                                                <span className="text-ink-300"> · </span>
                                                <span>{l.product_sku}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {/* Potmaat */}
                                <div
                                    className="font-mono text-[11px] font-semibold text-ink-700 px-1.5 py-0.5 bg-ink-100 rounded"
                                    data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-pot`}
                                >
                                    {l.pot_size || (isMerged ? "mix" : "—")}
                                </div>
                                {/* Aantal + samenvoeg-actie */}
                                <div className="text-right relative">
                                    <div
                                        className={`font-mono font-bold leading-none ${overflow ? "text-red-600" : "text-ink-900"}`}
                                        data-testid={`cart-${cartIdx + 1}-layer-${li + 1}-qty`}
                                    >
                                        {l.quantity}
                                    </div>
                                    {l.is_partial && !isMerged && otherPartials.length > 0 && (
                                        <MergeButtonWithPicker
                                            layer={l}
                                            cartIdx={cartIdx}
                                            li={li}
                                            otherPartials={otherPartials}
                                            open={isPickerOpen}
                                            onOpen={() => setMergePicker(isPickerOpen ? null : li)}
                                            onClose={() => setMergePicker(null)}
                                            onMergeLayers={onMergeLayers}
                                        />
                                    )}
                                    {isMerged && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onUnmergeLayer && onUnmergeLayer(li);
                                            }}
                                            data-testid={`unmerge-btn-${cartIdx + 1}-${li + 1}`}
                                            className="font-mono text-[9px] uppercase text-florefy-700 mt-0.5 hover:underline cursor-pointer"
                                            title="Maak samenvoeging ongedaan"
                                        >
                                            splits
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                    <div className="cart-base"></div>
                </div>
            </div>
        </div>
    );
}


// helpers
function cloneCarts(carts) {
    return carts.map((c) => ({
        ...c,
        layers: c.layers.map((l) => ({ ...l })),
        products_on_cart: [...(c.products_on_cart || [])],
    }));
}
function renumber(carts) {
    carts.forEach((c, i) => {
        c.cart_no = i + 1;
        c.layers.forEach((l, j) => (l.layer_no = j + 1));
        c.total_units = c.layers.reduce((s, l) => s + l.quantity, 0);
        c.products_on_cart = Array.from(new Set(c.layers.map((l) => l.product_id)));
    });
}

function cartHasOverflow(cart) {
    if (!cart?.layers?.length) return false;
    const gaps = cart.layers.map((layer) => layer.gap_units || layer.stack_rank || 0);
    const positions = computeShiftedPlatePositions(gaps);
    const maxPosition = maxPositionFor(!!cart.risers);
    return cart.layers.some((_, layerIndex) => (
        layerIndex > 0 && positions[layerIndex - 1] > maxPosition
    ));
}

function cartHasMixedLocations(cart) {
    const locations = new Set(
        (cart?.layers || []).map((layer) => layer.location_id).filter(Boolean)
    );
    if (cart?.location_id) locations.add(cart.location_id);
    return locations.size > 1;
}

function AddCartModal({ existingGroups, onClose, onAdd }) {
    return (
        <div
            className="fixed inset-0 bg-florefy-900/40 flex items-center justify-center z-50 p-4"
            onClick={onClose}
            data-testid="add-cart-modal"
        >
            <div
                className="bg-white border border-border rounded-md w-full max-w-md p-6 mx-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-lg font-semibold text-ink-900 mb-2">Kar toevoegen</h3>
                <p className="text-sm text-ink-500 mb-4">
                    Kies de locatie waar deze nieuwe lege kar bij hoort. De kar verschijnt
                    direct in die locatie-groep zodat je er lagen naartoe kunt slepen.
                </p>
                <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
                    {existingGroups.length === 0 && (
                        <div className="text-xs text-ink-500 italic">
                            Geen locaties beschikbaar — er staan nog geen karren met locatie.
                        </div>
                    )}
                    {existingGroups.map((g) => (
                        <button
                            key={g.id}
                            type="button"
                            onClick={() => onAdd(g.id, g.code, g.name)}
                            className="w-full text-left px-4 py-3 rounded-sm border border-border bg-white hover:bg-florefy-50 hover:border-florefy-900 transition-colors flex items-center gap-3"
                            data-testid={`add-cart-loc-${g.code}`}
                        >
                            <div className="h-8 w-8 rounded-sm bg-florefy-900 text-white font-mono font-bold text-sm flex items-center justify-center">
                                {g.code}
                            </div>
                            <div>
                                <div className="text-sm font-medium text-ink-900">{g.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
                                    Locatie {g.code}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
                <div className="flex justify-between items-center gap-2 pt-3 border-t border-border">
                    <button
                        type="button"
                        onClick={() => onAdd("", "", "")}
                        className="text-xs text-ink-500 hover:text-florefy-900 underline"
                        data-testid="add-cart-no-loc"
                    >
                        Zonder locatie
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-ink-50"
                        data-testid="btn-close-add-cart"
                    >
                        Annuleer
                    </button>
                </div>
            </div>
        </div>
    );
}


/**
 * "+ samenv." knop met een portal-popover die zelfs binnen scroll-containers
 * niet wegvalt. Gebruikt PortalPopover om de keuze-lijst in document.body te
 * renderen, met positie t.o.v. de knop-ref.
 */
function MergeButtonWithPicker({ layer, cartIdx, li, otherPartials, open, onOpen, onClose, onMergeLayers }) {
    const btnRef = useRef(null);
    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onOpen();
                }}
                data-testid={`merge-btn-${cartIdx + 1}-${li + 1}`}
                className="font-mono text-[9px] uppercase text-florefy-700 mt-0.5 hover:underline cursor-pointer"
                title="Voeg restje samen met andere partial op deze kar"
            >
                + samenv.
            </button>
            <PortalPopover
                open={open}
                onClose={onClose}
                anchorRef={btnRef}
                align="right"
                width={224}
                className="p-1 text-left"
                testid={`merge-picker-${cartIdx + 1}-${li + 1}`}
            >
                <div className="px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-ink-500">
                    Samenvoegen met…
                </div>
                {otherPartials.map(({ x, idx }) => {
                    const xFill = (x.quantity || 0) / Math.max(1, x.units_per_layer || 1);
                    const myFill = (layer.quantity || 0) / Math.max(1, layer.units_per_layer || 1);
                    const fits = xFill + myFill <= 1.0001;
                    return (
                        <button
                            key={idx}
                            type="button"
                            disabled={!fits}
                            onClick={() => {
                                onClose();
                                onMergeLayers && onMergeLayers(li, idx);
                            }}
                            className={`block w-full text-left px-2 py-1.5 text-[12px] rounded-sm ${
                                fits ? "hover:bg-florefy-50 text-ink-900" : "text-ink-300 cursor-not-allowed"
                            }`}
                            data-testid={`merge-pick-${cartIdx + 1}-${li + 1}-with-${idx + 1}`}
                        >
                            <div className="truncate font-medium">
                                Laag {idx + 1}: {abbreviateLabel(x.product_name, x.product_cultivar)}
                            </div>
                            <div className="font-mono text-[10px] text-ink-500">
                                {x.quantity} stuks ({(xFill * 100).toFixed(0)}%) {fits ? "" : "→ past niet"}
                            </div>
                        </button>
                    );
                })}
            </PortalPopover>
        </>
    );
}
